/**
 * @module components/Audit/AuditEntryCard
 * @description Tarjeta visual para una entrada individual del trail de auditoría.
 *
 * Renderiza una entrada de `AuditEntry` con:
 * - Ícono y color según el tipo de acción (`AUDIT_ICONS`, `AUDIT_COLORS`)
 * - Timestamp formateado con `formatRelativeTime()`
 * - Usuario y email del autor del cambio
 * - Valores anterior y nuevo (si aplica) con flecha de transición
 * - Indicador visual de criticidad (archivos subidos, usuarios afectados, etc.)
 *
 * @see AuditLog que renderiza una lista de estas tarjetas
 * @see auditFormatters.ts para los íconos, colores y helpers de formateo
 */

import React from 'react';
import { Clock, User, ArrowRight } from 'lucide-react';
import { AuditEntry } from '../../types/iso';
import {
  getActionType,
  generateActionDescription,
  generateDetailedSummary,
  parseAuditDetails,
  formatDate,
  AUDIT_ICONS,
  AUDIT_COLORS
} from '../../utils/auditFormatters';

// =====================================================
// INTERFACES
// =====================================================

interface AuditEntryCardProps {
  /** Entrada de auditoría a mostrar */
  entry: AuditEntry;
  /** Estilo compacto (por defecto: false) */
  compact?: boolean;
}

// =====================================================
// COMPONENTE PRINCIPAL
// =====================================================

export function AuditEntryCard({
  entry,
  compact = false
}: AuditEntryCardProps) {
  // =====================================================
  // PROCESAMIENTO DE DATOS
  // =====================================================

  const actionType = getActionType(entry.action, entry.details);
  const icon = AUDIT_ICONS[actionType];
  const colorClasses = AUDIT_COLORS[actionType];
  const description = generateActionDescription(entry.action, entry.details, entry.user.name);
  const detailedSummary = generateDetailedSummary(entry.action, entry.details, entry.user.name);
  const parsedDetails = entry.details ? parseAuditDetails(entry.details) : null;
  const timestamp = formatDate(entry.timestamp);

  // =====================================================
  // LOGGING (solo en desarrollo)
  // =====================================================

  React.useEffect(() => {
    console.log('📋 [AUDIT ENTRY] Renderizando entrada:', {
      action: entry.action,
      actionType,
      description,
      user: entry.user.name,
      timestamp: entry.timestamp,
      hasChanges: parsedDetails?.parsedChanges?.length || 0
    });
  }, [entry, actionType, description, parsedDetails]);

  // =====================================================
  // RENDERIZADO
  // =====================================================

  return (
    <div className={`border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden ${
      compact ? 'p-3' : 'p-4'
    }`}>
      {/* ===== ENCABEZADO ===== */}
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-start space-x-3 flex-1">
          {/* Icono de acción */}
          <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-lg ${colorClasses}`}>
            {icon}
          </div>

          {/* Información principal */}
          <div className="flex-1 min-w-0">
            {/* Descripción de la acción */}
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white truncate">
              {description}
            </h4>

            {/* Usuario y timestamp */}
            <div className="flex items-center space-x-2 mt-1 text-xs text-gray-500 dark:text-gray-400">
              <User className="w-3 h-3" />
              <span>{entry.user.name}</span>
              <span>•</span>
              <Clock className="w-3 h-3" />
              <span>{timestamp}</span>
            </div>
          </div>
        </div>

        {/* Badge de tipo de acción */}
        <div className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${colorClasses}`}>
          {actionType === 'insert' && 'Creación'}
          {actionType === 'update' && 'Actualización'}
          {actionType === 'delete' && 'Eliminación'}
          {actionType === 'assign' && 'Asignación'}
          {actionType === 'complete' && 'Completado'}
          {actionType === 'upload' && 'Carga'}
          {actionType === 'other' && 'Otro'}
        </div>
      </div>

      {/* ===== DETALLES DE CAMBIOS ===== */}
      {parsedDetails && parsedDetails.parsedChanges.length > 0 && (
        <div className="mt-3 space-y-2">
          {parsedDetails.parsedChanges.map((change, index) => (
            <div
              key={index}
              className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700"
            >
              {/* Nombre del campo */}
              <div className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
                {change.fieldLabel}
              </div>

              {/* Cambio: Valor antiguo → Valor nuevo */}
              <div className="flex items-center space-x-2 text-sm">
                {/* Valor antiguo */}
                <div className="flex-1 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded px-3 py-2">
                  <div className="text-xs text-red-600 dark:text-red-400 font-medium mb-1">
                    Anterior
                  </div>
                  <div className="text-gray-900 dark:text-white break-words">
                    {change.oldValue}
                  </div>
                </div>

                {/* Flecha */}
                <ArrowRight className="w-5 h-5 text-gray-400 flex-shrink-0" />

                {/* Valor nuevo */}
                <div className="flex-1 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded px-3 py-2">
                  <div className="text-xs text-green-600 dark:text-green-400 font-medium mb-1">
                    Nuevo
                  </div>
                  <div className="text-gray-900 dark:text-white break-words">
                    {change.newValue}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ===== RESUMEN DETALLADO ===== */}
      {detailedSummary.items.length > 0 && (
        <div className="mt-3 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
          {/* Título del resumen */}
          <div className="flex items-center space-x-2 mb-3">
            <div className="text-2xl">📝</div>
            <div className="flex-1">
              <h5 className="text-sm font-bold text-gray-900 dark:text-white">
                Resumen del cambio
              </h5>
              {detailedSummary.description && (
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  {detailedSummary.description}
                </p>
              )}
            </div>
          </div>

          {/* Lista de items del resumen */}
          <div className="space-y-2">
            {detailedSummary.items.map((item, index) => (
              <div
                key={index}
                className={`flex items-start space-x-3 p-2 rounded-lg transition-all ${
                  item.highlight
                    ? 'bg-yellow-100 dark:bg-yellow-900/30 border border-yellow-300 dark:border-yellow-700'
                    : 'bg-white/50 dark:bg-gray-800/50'
                }`}
              >
                {/* Icono */}
                <div className="text-lg flex-shrink-0 mt-0.5">
                  {item.icon}
                </div>

                {/* Contenido */}
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                    {item.label}
                  </div>
                  <div className="text-sm text-gray-900 dark:text-white font-medium break-words">
                    {item.value}
                  </div>
                </div>

                {/* Badge de "cambio principal" si está resaltado */}
                {item.highlight && (
                  <div className="flex-shrink-0">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-200 dark:bg-yellow-800 text-yellow-800 dark:text-yellow-200">
                      Principal
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// =====================================================
// COMPONENTE DE GRUPO TEMPORAL
// =====================================================

interface AuditGroupProps {
  /** Etiqueta del grupo (ej: "Hoy", "Ayer") */
  label: string;
  /** Entradas de auditoría en este grupo */
  entries: AuditEntry[];
  /** Estilo compacto */
  compact?: boolean;
}

export function AuditGroup({ label, entries, compact = false }: AuditGroupProps) {
  return (
    <div className="mb-6">
      {/* Etiqueta del grupo */}
      <div className="flex items-center mb-3">
        <div className="text-sm font-semibold text-gray-700 dark:text-gray-300">
          {label}
        </div>
        <div className="flex-1 ml-3 border-t border-gray-200 dark:border-gray-700" />
        <div className="ml-3 text-xs text-gray-500 dark:text-gray-400">
          {entries.length} {entries.length === 1 ? 'entrada' : 'entradas'}
        </div>
      </div>

      {/* Lista de entradas */}
      <div className="space-y-3">
        {entries.map((entry) => (
          <AuditEntryCard
            key={entry.id}
            entry={entry}
            compact={compact}
          />
        ))}
      </div>
    </div>
  );
}
